import { useState, useCallback } from "react";

interface AuthState {
  token: string | null;
  refreshToken: string | null;
  userId: string | null;
  email: string | null;
  role: string | null;
  sid: string | null;
  aal: number;
  stepupUntil: number | null;
}

export interface PendingMfaChallenge {
  challengeId: string;
  methods: string[];
  preferredMethod: string;
  requiresEnrollment: boolean;
}

export interface CompletedPasswordLogin {
  completed: true;
  requiredActions: string[];
}

type PasswordLoginStartResponse = PendingMfaChallenge | CompletedPasswordLogin;

const STORAGE_KEY = "agentme-adam-auth";

function getStoredAuth(): AuthState {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as Partial<AuthState>;
      return {
        token: parsed.token ?? null,
        refreshToken: parsed.refreshToken ?? null,
        userId: parsed.userId ?? null,
        email: parsed.email ?? null,
        role: parsed.role ?? null,
        sid: parsed.sid ?? null,
        aal: typeof parsed.aal === "number" ? parsed.aal : 0,
        stepupUntil: typeof parsed.stepupUntil === "number" ? parsed.stepupUntil : null,
      };
    }
  } catch {
    // ignore
  }
  return {
    token: null,
    refreshToken: null,
    userId: null,
    email: null,
    role: null,
    sid: null,
    aal: 0,
    stepupUntil: null,
  };
}

function persistAuth(state: AuthState): void {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

function clearPersistedAuth(): void {
  sessionStorage.removeItem(STORAGE_KEY);
}

function decodeBase64Url(input: string): Uint8Array {
  const normalized = input.replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
  const raw = atob(padded);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) {
    bytes[i] = raw.charCodeAt(i);
  }
  return bytes;
}

function toBase64Url(bytes: ArrayBuffer): string {
  const binary = String.fromCharCode(...new Uint8Array(bytes));
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

function encodeUtf8(value: string): Uint8Array {
  return new TextEncoder().encode(value);
}

export function useAuth() {
  const [auth, setAuthState] = useState<AuthState>(getStoredAuth);

  function normalizeSessionPayload(data: {
    token?: string;
    accessToken?: string;
    refreshToken?: string;
    claims?: {
      sub?: string;
      email?: string;
      role?: string;
      sid?: string;
      aal?: number;
      stepup_until?: number | null;
    };
  }): AuthState {
    return {
      token: data.token ?? data.accessToken ?? null,
      refreshToken: data.refreshToken ?? null,
      userId: data.claims?.sub ?? null,
      email: data.claims?.email ?? null,
      role: data.claims?.role ?? null,
      sid: data.claims?.sid ?? null,
      aal: data.claims?.aal ?? 1,
      stepupUntil: data.claims?.stepup_until ?? null,
    };
  }

  const login = useCallback(async (email: string, password: string) => {
    // Legacy compatibility path retained for existing tooling.
    const res = await fetch("/api/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: "Login failed" }));
      throw new Error(err.error || "Login failed");
    }
    const data = await res.json();
    const state: AuthState = {
      ...normalizeSessionPayload(data),
      userId: data.userId ?? data.claims?.sub ?? null,
      email: data.email ?? data.claims?.email ?? null,
      role: data.role ?? data.claims?.role ?? null,
    };
    setAuthState(state);
    persistAuth(state);
    return state;
  }, []);

  const startPasswordLogin = useCallback(async (identifier: string, password: string) => {
    const res = await fetch("/api/auth/login/password/start", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ identifier, password, audience: "adam" }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: "Login start failed" }));
      throw new Error(err.error || "Login start failed");
    }
    const data = (await res.json()) as
      | PendingMfaChallenge
      | {
          token: string;
          refreshToken?: string;
          claims?: {
            sub?: string;
            email?: string;
            role?: string;
            sid?: string;
            aal?: number;
            stepup_until?: number | null;
          };
          requiredActions?: string[];
        };

    if (("token" in data && data.token) || ("accessToken" in data && data.accessToken)) {
      const state = normalizeSessionPayload(data);
      setAuthState(state);
      persistAuth(state);
      return { completed: true, requiredActions: (data.requiredActions ?? []) as string[] };
    }

    return data as PasswordLoginStartResponse;
  }, []);

  const completeWithTotp = useCallback(
    async (challengeId: string, code: string, method: "totp" | "recovery_code" = "totp") => {
      const res = await fetch("/api/auth/totp/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ challengeId, code, method }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: "MFA verification failed" }));
        throw new Error(err.error || "MFA verification failed");
      }
      const data = await res.json();
      const state = {
        ...normalizeSessionPayload(data),
        aal: data.claims?.aal ?? 2,
      };
      setAuthState(state);
      persistAuth(state);
      return { state, requiredActions: (data.requiredActions ?? []) as string[] };
    },
    [],
  );

  const completeWithPasskey = useCallback(async (challengeId: string) => {
    if (!("PublicKeyCredential" in window)) {
      throw new Error("Passkeys are not available in this browser");
    }

    const optionsRes = await fetch("/api/auth/passkeys/auth/options", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ challengeId }),
    });
    if (!optionsRes.ok) {
      const err = await optionsRes.json().catch(() => ({ error: "Passkey options failed" }));
      throw new Error(err.error || "Passkey options failed");
    }

    const optionsData = (await optionsRes.json()) as {
      challengeId: string;
      publicKeyOptions: {
        challenge: string;
        rpId?: string;
        timeout?: number;
        userVerification?: "required" | "preferred" | "discouraged";
        allowCredentials: Array<{ id: string; type: "public-key" }>;
      };
    };

    const credential = (await navigator.credentials.get({
      publicKey: {
        challenge: new TextEncoder().encode(optionsData.publicKeyOptions.challenge),
        rpId: optionsData.publicKeyOptions.rpId,
        timeout: optionsData.publicKeyOptions.timeout,
        userVerification: optionsData.publicKeyOptions.userVerification,
        allowCredentials: optionsData.publicKeyOptions.allowCredentials.map((entry) => ({
          id: decodeBase64Url(entry.id),
          type: entry.type,
        })),
      },
    })) as PublicKeyCredential | null;

    if (!credential) {
      throw new Error("Passkey authentication was cancelled");
    }

    const verifyRes = await fetch("/api/auth/passkeys/auth/verify", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        challengeId: optionsData.challengeId,
        credentialId: toBase64Url(credential.rawId),
      }),
    });

    if (!verifyRes.ok) {
      const err = await verifyRes.json().catch(() => ({ error: "Passkey verification failed" }));
      throw new Error(err.error || "Passkey verification failed");
    }

    const data = await verifyRes.json();
    const state = {
      ...normalizeSessionPayload(data),
      aal: data.claims?.aal ?? 2,
    };

    setAuthState(state);
    persistAuth(state);
    return { state, requiredActions: (data.requiredActions ?? []) as string[] };
  }, []);

  const enrollPasskey = useCallback(async (loginChallengeId: string, label = "Passkey") => {
    if (!("PublicKeyCredential" in window)) {
      throw new Error("Passkeys are not available in this browser");
    }

    const optionsRes = await fetch("/api/auth/passkeys/register/options", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ loginChallengeId, label }),
    });
    if (!optionsRes.ok) {
      const err = await optionsRes
        .json()
        .catch(() => ({ error: "Passkey enrollment options failed" }));
      throw new Error(err.error || "Passkey enrollment options failed");
    }

    const optionsData = (await optionsRes.json()) as {
      challengeId: string;
      publicKeyOptions: {
        challenge: string;
        rp: PublicKeyCredentialRpEntity;
        user: { id: string; name: string; displayName: string };
        timeout?: number;
        attestation?: AttestationConveyancePreference;
      };
    };

    const credential = (await navigator.credentials.create({
      publicKey: {
        challenge: encodeUtf8(optionsData.publicKeyOptions.challenge),
        rp: optionsData.publicKeyOptions.rp,
        user: {
          id: encodeUtf8(optionsData.publicKeyOptions.user.id),
          name: optionsData.publicKeyOptions.user.name,
          displayName: optionsData.publicKeyOptions.user.displayName,
        },
        timeout: optionsData.publicKeyOptions.timeout,
        attestation: optionsData.publicKeyOptions.attestation ?? "none",
        pubKeyCredParams: [
          { alg: -7, type: "public-key" }, // ES256
          { alg: -257, type: "public-key" }, // RS256
        ],
      },
    })) as PublicKeyCredential | null;

    if (!credential) {
      throw new Error("Passkey enrollment was cancelled");
    }

    const verifyRes = await fetch("/api/auth/passkeys/register/verify", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        challengeId: optionsData.challengeId,
        credentialId: toBase64Url(credential.rawId),
      }),
    });

    if (!verifyRes.ok) {
      const err = await verifyRes
        .json()
        .catch(() => ({ error: "Passkey enrollment verification failed" }));
      throw new Error(err.error || "Passkey enrollment verification failed");
    }

    const data = await verifyRes.json();
    const state = {
      ...normalizeSessionPayload(data),
      aal: data.claims?.aal ?? 2,
    };

    setAuthState(state);
    persistAuth(state);
    return { state, requiredActions: (data.requiredActions ?? []) as string[] };
  }, []);

  const refreshSession = useCallback(async () => {
    if (!auth.refreshToken) {
      throw new Error("No refresh token available");
    }

    const res = await fetch("/api/auth/session/refresh", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken: auth.refreshToken }),
    });

    if (!res.ok) {
      clearPersistedAuth();
      setAuthState(getStoredAuth());
      throw new Error("Session refresh failed");
    }

    const data = await res.json();
    const state: AuthState = {
      ...normalizeSessionPayload(data),
      userId: data.claims?.sub ?? auth.userId,
      email: data.claims?.email ?? auth.email,
      role: data.claims?.role ?? auth.role,
      sid: data.claims?.sid ?? auth.sid,
      aal: data.claims?.aal ?? auth.aal,
      stepupUntil: data.claims?.stepup_until ?? auth.stepupUntil,
    };
    setAuthState(state);
    persistAuth(state);
    return state;
  }, [auth.aal, auth.email, auth.refreshToken, auth.role, auth.sid, auth.stepupUntil, auth.userId]);

  const logout = useCallback(async () => {
    try {
      if (auth.token) {
        await fetch("/api/auth/logout", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${auth.token}`,
          },
          body: JSON.stringify({ sessionId: auth.sid }),
        });
      }
    } catch {
      // ignore network failures on logout
    }

    const empty = {
      token: null,
      refreshToken: null,
      userId: null,
      email: null,
      role: null,
      sid: null,
      aal: 0,
      stepupUntil: null,
    } satisfies AuthState;
    setAuthState(empty);
    clearPersistedAuth();
  }, [auth.sid, auth.token]);

  return {
    ...auth,
    isAuthenticated: !!auth.token,
    login,
    startPasswordLogin,
    completeWithTotp,
    completeWithPasskey,
    enrollPasskey,
    refreshSession,
    logout,
  };
}
